import { EditorState } from 'draft-js';
import { Form, Formik } from 'formik';
import React from 'react';
import DatePicker from 'react-datepicker';
import styled from 'styled-components';
import { Breakpoints } from '../../../app/StyleGuide';
import { Buttons, PrimaryButton, ToggleButton } from '../../../components/Buttons';
import ErrorMessage from '../../../components/ErrorMessage';
import {
  FormField,
  FormFieldContainer,
  FormFieldDate,
  FormFieldMaxCharCountStatus,
  FormFieldSet,
  FormGroupContainer,
  Header,
  Input,
  Label,
} from '../../../components/FormElements';
import HelpAndInfoBox from '../../../components/HelpAndInfoBox';
import ImageCrop from '../../../components/ImageCrop';
import { CurriculumVolumeImagePlaceholder } from '../../../components/Layout';
import RichTextEditor from '../../../containers/RichTextEditor';
import { GetCurriculumResponse } from '../api/getIndividualCurriculum';
import { useManageCurriculumVolumeForm } from '../hooks/useManageCurriculumVolumeForm';
import { Volume } from '../models/volume';

const AvailabilityEndDateContainer = styled(FormFieldDate)`
  .react-datepicker-popper {
    z-index: 2;
  }

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    justify-content: center;
    grid-template-columns: 0.5fr;
  }
`;

type ManageCurriculumVolumeFormProps = {
  volume?: Volume;
  curriculum: GetCurriculumResponse;
  userId: string;
  handleSubmit: (submitPromise: Promise<void>) => void;
};

export const ManageCurriculumVolumeForm = ({
  volume,
  curriculum,
  userId,
  handleSubmit,
}: ManageCurriculumVolumeFormProps) => {
  const hasSections = !!volume?.sections?.length;

  const {
    initialFormValues,
    initialFormStatus,
    validationSchema,
    updateTextEditor,
    toggleAvailabilityEndDate,
    submitForm,
  } = useManageCurriculumVolumeForm(volume);

  return (
    <Formik
      enableReinitialize
      initialValues={initialFormValues}
      initialStatus={initialFormStatus}
      validationSchema={validationSchema}
      onSubmit={values => {
        handleSubmit(submitForm(values, curriculum.id, userId, volume?._id, volume?.image));
      }}
    >
      {({ values, status, errors, touched, isSubmitting, setFieldValue, setStatus }) => (
        <Form data-qa-hook="manageCurriculumVolumeView">
          <div className="grid-container grid-col-4 grid-sm-col-8 grid-col-gap-24" style={{ margin: '32px 0' }}>
            <div className="grid-col-span-4">
              <Header>{volume ? 'Edit' : 'New'} Curriculum Volume</Header>
            </div>
            <div className="grid-col-span-4">
              {!volume && (
                <HelpAndInfoBox description="Once a new volume is saved, volume headers will be available for creating and editing." />
              )}
              {volume && hasSections && (
                <HelpAndInfoBox description="To copy all of the volume headers from the previous volume, use the Add Header button at the bottom of the page." />
              )}
            </div>
          </div>
          <div className="grid-container grid-col-4 grid-sm-col-8 grid-col-gap-24" style={{ margin: '32px 0' }}>
            <div className="grid-col-span-4 grid-sm-col-span-8">
              <FormFieldSet name={`${curriculum.name}: ${curriculum.brand}`} />
            </div>
            <FormFieldContainer className="grid-col-span-4 grid-sm-col-span-3">
              <Label>Poster</Label>
              <CurriculumVolumeImagePlaceholder actionable={!values.image}>
                <ImageCrop
                  defaultImage={volume?.imageUrl}
                  aspectRatio={258 / 332}
                  onUpdate={img => setFieldValue('image', img)}
                />
              </CurriculumVolumeImagePlaceholder>
              {errors.image && touched.image && <ErrorMessage>An image is required</ErrorMessage>}
            </FormFieldContainer>
            <div className="grid-col-span-4 grid-sm-col-span-5">
              <div className="grid-container grid-col-1 grid-sm-col-2 grid-col-gap-24">
                <FormFieldDate>
                  <Label>Start Date</Label>
                  <DatePicker
                    data-qa-hook="startDatePicker"
                    selected={values.startDate}
                    onChange={date => setFieldValue('startDate', date)}
                    customInput={<Input />}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="Select Date"
                  />
                  {touched.startDate && errors.startDate && <ErrorMessage>A start date is required</ErrorMessage>}
                </FormFieldDate>
                <FormFieldDate>
                  <Label>End Date</Label>
                  <DatePicker
                    data-qa-hook="endDatePicker"
                    selected={values.endDate}
                    onChange={date => setFieldValue('endDate', date)}
                    customInput={<Input />}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="Select Date"
                  />
                  {touched.endDate && errors.endDate && <ErrorMessage>{errors.endDate}</ErrorMessage>}
                </FormFieldDate>
              </div>
              <FormFieldMaxCharCountStatus
                label="Subtitle"
                name="name"
                type="text"
                placeholder="Enter Subtitle"
                maxLength={128}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => setFieldValue('name', e.target.value)}
              />
              <FormField
                label="Poster Url"
                name="posterUrl"
                type="text"
                placeholder="Enter Poster Url"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('posterUrl', e.target.value)}
              />
              <FormFieldMaxCharCountStatus
                label="Volume Number"
                name="number"
                type="number"
                placeholder="Enter Volume Number"
                maxLength={48}
                min="1"
                step="1"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('number', e.target.value)}
              />
              <FormFieldContainer>
                <FormFieldMaxCharCountStatus
                  label="Quarter/Date"
                  name="periodTitle"
                  type="text"
                  placeholder="Enter Quarter/Date"
                  maxLength={128}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => setFieldValue('periodTitle', e.target.value)}
                />
                {errors.periodTitle && <ErrorMessage>{errors.periodTitle}</ErrorMessage>}
              </FormFieldContainer>
              {!!curriculum.offCycleEnabled && (
                <>
                  <FormFieldContainer>
                    <Label>Reset Schedule Availability</Label>
                    <FormGroupContainer>
                      <p>Adjust the availability of when the schedule can be reset for this volume.</p>
                      <ToggleButton
                        isOn={status.isAlwaysAvailable}
                        onClick={() =>
                          toggleAvailabilityEndDate(status.isAlwaysAvailable, setFieldValue, status, setStatus)
                        }
                      >
                        Always Available
                      </ToggleButton>
                    </FormGroupContainer>
                  </FormFieldContainer>
                  <FormFieldContainer>
                    <AvailabilityEndDateContainer className="grid-container grid-col-1">
                      <div className={`grid-span-col-1${status.isAlwaysAvailable ? ' disabled' : ''}`}>
                        <Label>Availability End Date</Label>
                        <DatePicker
                          data-qa-hook="availabilityEndDatePicker"
                          selected={values.availabilityEndDate}
                          onChange={date => setFieldValue('availabilityEndDate', date)}
                          customInput={<Input />}
                          dateFormat="MM/dd/yyyy"
                          disabled={status.isAlwaysAvailable}
                          placeholderText="Select Date"
                        />
                        {values.availabilityEndDate === null &&
                          touched.availabilityEndDate &&
                          errors.availabilityEndDate && <ErrorMessage>{errors.availabilityEndDate}</ErrorMessage>}
                      </div>
                    </AvailabilityEndDateContainer>
                  </FormFieldContainer>
                </>
              )}
              <FormFieldContainer>
                <Label>Volume Description</Label>
                <RichTextEditor
                  id="description"
                  name="description"
                  editorState={status.descriptionEditorState}
                  setEditorState={(es: EditorState) =>
                    updateTextEditor(es, 'description', setFieldValue, status, setStatus)
                  }
                  config={{
                    placeholder: 'Enter Description',
                    height: '200px',
                  }}
                />
              </FormFieldContainer>
              <div className="grid-col-span-4 grid-sm-col-span-2">
                <Buttons style={{ justifyContent: 'flex-end' }}>
                  <PrimaryButton data-qa-hook="submit" type="submit" disabled={isSubmitting} operating={isSubmitting}>
                    Save Volume
                  </PrimaryButton>
                </Buttons>
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};
